import {
    Box,
    Stepper as ChakraStepper,
    Stack,
    Step,
    StepDescription,
    StepIcon,
    StepIndicator,
    StepSeparator,
    StepStatus,
    useSteps,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

type ResponsiveValue<T> = T | Record<string, T>;

type PropTypes = {
    step?: number;
    isEnrol?: boolean;
    isAfterEnrolmentPathway?: boolean;
    isReEnrolmentPathway?: boolean;
    isRegistration?: boolean;
    hasStepSeparator?: boolean;
    marginTop?: ResponsiveValue<number | string>;
    marginBottom?: ResponsiveValue<number | string>;
    marginLeft?: ResponsiveValue<number | string>;
    mx?: number;
    maxW?: ResponsiveValue<number[] | string[]>;
};

const Stepper = ({
    step,
    isEnrol = false,
    isAfterEnrolmentPathway = false,
    isReEnrolmentPathway = false,
    isRegistration = false,
    hasStepSeparator = false,
    marginTop = 0,
    marginBottom = 8,
    marginLeft = 0,
    mx = 0,
    maxW = ["70px", "100px", "150px"],
}: PropTypes) => {
    let steps;
    const { t } = useTranslation();
    if (isAfterEnrolmentPathway) {
        steps = t(
            "stepper.afterEnrolmentPathway",
            "1. Subject selection, 2. Subject evaluation"
        ).split(",");
    } else if (isEnrol) {
        steps = t(
            "stepper.enrol",
            "1. Your details, 2. Set password, 3. Student enrolment"
        ).split(",");
    } else if (isReEnrolmentPathway) {
        steps = t(
            "stepper.reEnrol",
            "1. Enrolment, 2. Subject selection, 3. Subject evaluation"
        ).split(",");
    } else if (isRegistration) {
        steps = [
            "Answer questions about your student",
            "We'll review and prepare your documents",
            "Download & submit your documents"
        ];
    } else {
        steps = t(
            "stepper.checkout",
            "1. Your details, 2. Student details, 3. Enrolment details, 4. Payment"
        ).split(",");
    }

    const { activeStep } = useSteps({
        index: step ? step - 1 : 0,
        count: steps.length
    });

    return (
        <Box
            display="block"
            position="relative"
        >
            <ChakraStepper
                size="xs"
                index={activeStep}
                gap={0}
                mt={marginTop}
                mb={marginBottom}
                ml={marginLeft}
            >
                {steps.map((step, index) => (
                    <Step<React.ElementType>
                        key={step}
                        position="relative"
                        gap={0}
                        flexShrink={0}
                        _notFirst={{
                            _before: {
                                content: "''",
                                position: "absolute",
                                top: 2,
                                height: "1px",
                                width: "50%",
                                bg: "gray.300",
                                zIndex: steps.length === 2 ? 1 : -1
                            }
                        }}
                        _notLast={{
                            _after: {
                                content: "''",
                                position: "absolute",
                                top: 2,
                                right: 0,
                                height: "1px",
                                width: "50%",
                                bg: "gray.300",
                                zIndex: steps.length === 2 ? 1 : -1
                            }
                        }}
                    >
                        <Stack
                            align="center"
                            justify="flex-start"
                            pl={
                                !hasStepSeparator && index !== 0 ? 3 : undefined
                            }
                            pr={
                                !hasStepSeparator && index !== steps.length - 1
                                    ? 3
                                    : undefined
                            }
                            position="relative"
                        >
                            {hasStepSeparator ? (
                                <StepIndicator
                                    sx={{
                                        "[data-status=active] &": {
                                            background: "#66CB9F",
                                            border: "2px",
                                            borderColor: "gray.300"
                                        },
                                        "[data-status=complete] &": {
                                            background: "#66CB9F",
                                            borderColor: "#66CB9F"
                                        }
                                    }}
                                >
                                    <StepStatus complete={<StepIcon />} />
                                </StepIndicator>
                            ) : (
                                <StepIndicator>
                                    <StepStatus />
                                </StepIndicator>
                            )}
                            <StepDescription<React.ElementType>
                                fontFamily="secondary"
                                textAlign="center"
                                fontWeight={
                                    activeStep === index ? "600" : "400"
                                }
                                color={
                                    activeStep === index
                                        ? "gray.800"
                                        : "gray.600"
                                }
                                mx={mx}
                                maxW={maxW}
                            >
                                {step}
                            </StepDescription>
                            {hasStepSeparator && (
                                <StepSeparator
                                    style={{
                                        position: "absolute",
                                        top: "7px",
                                        left: "50%",
                                        right: "-50%",
                                        height: "2px",
                                        background:
                                            activeStep > index
                                                ? "#66CB9F"
                                                : "gray.300"
                                    }}
                                />
                            )}
                        </Stack>
                    </Step>
                ))}
            </ChakraStepper>
        </Box>
    );
};

export default Stepper;
